import { Button, Modal, useToast } from "@design";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { createTeamMutation, errorMessage, schemas, type TeamResponse, updateTeamMutation } from "@/api";
import { useAppForm } from "@/components/form/form";
import { useRefreshTeams } from "./useRefreshTeams";

// Name and description limits come from the generated TeamCreate schema.
const create = schemas.zTeamCreate.shape;
const teamSchema = (nameRequired: string) =>
  z.object({
    name: z.string().trim().min(1, nameRequired).pipe(create.name),
    description: z.string().max(350),
  });

/**
 * Create a team (no `team`) or rename / describe one, for platform admins. Mount only while open so it starts fresh.
 * `onCreated` gets the new team's uuid.
 */
export function TeamFormModal({
  team,
  onClose,
  onCreated,
}: Readonly<{ team?: TeamResponse; onClose: () => void; onCreated?: (uuid: string) => void }>) {
  const { t } = useTranslation("teams");
  const toast = useToast();
  const refreshTeams = useRefreshTeams();
  const createTeam = useMutation(createTeamMutation());
  const updateTeam = useMutation(updateTeamMutation());
  const title = team ? t("form.editTitle", { name: team.name }) : t("form.createTitle");

  const form = useAppForm({
    defaultValues: { name: team?.name ?? "", description: team?.description ?? "" },
    validators: { onChange: teamSchema(t("form.nameRequired")) },
    onSubmit: async ({ value }) => {
      const body = { name: value.name.trim(), description: value.description.trim() || null };
      try {
        if (team) {
          await updateTeam.mutateAsync({ path: { team_uuid: team.uuid }, body, throwOnError: true });
          toast.success(t("form.saved", { name: body.name }));
        } else {
          const res = await createTeam.mutateAsync({ body, throwOnError: true });
          toast.success(t("form.created", { name: body.name }));
          onCreated?.(res.data.uuid);
        }
        await refreshTeams();
        onClose();
      } catch (error) {
        toast.error(errorMessage(error, t(team ? "form.saveFailed" : "form.createFailed")));
      }
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      closeLabel={t("form.cancel")}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("form.cancel")}
          </Button>
          <form.Subscribe selector={(s) => s.isSubmitting}>
            {(submitting) => (
              <Button variant="primary" loading={submitting} onClick={() => void form.handleSubmit()}>
                {team ? t("form.save") : t("form.create")}
              </Button>
            )}
          </form.Subscribe>
        </>
      }
    >
      <form
        aria-label={title}
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <form.AppField name="name">
          {(field) => <field.TextField label={t("form.name")} required placeholder={t("form.namePlaceholder")} />}
        </form.AppField>
        <form.AppField name="description">
          {(field) => (
            <field.TextareaField
              label={t("form.description")}
              rows={3}
              placeholder={t("form.descriptionPlaceholder")}
            />
          )}
        </form.AppField>
      </form>
    </Modal>
  );
}
